import React, { useEffect, useState } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingButton } from '@mui/lab';

import {
  FAVORITE_CUISINES,
  ADVENTUROUS,
  ALlERGIES,
  COMFORT_LEVEL,
  DIETARY_RESTRICTIONS,
  MEAL_PREPARING_TIME,
  Strings,
} from '@app/constants';
import { selectLoginState } from '@app/modules/auth';
import { useRequestState } from '@app/hooks';
import { selectUserPreferences } from '../selectors';
import { getUserPreferences, updateUserPreferences } from '../slice';
import { IUserPreferences } from '../types';

interface ToggleOptionProps {
  option: string;
  selectedOptions: string[];
  setSelectedOptions: React.Dispatch<React.SetStateAction<string[]>>;
}

interface ToggleSingleOptionProps {
  option: string;
  selectedOption: string | null;
  setSelectedOption: React.Dispatch<React.SetStateAction<string | null>>;
}

const ToggleOption: React.FC<ToggleOptionProps> = ({
  option,
  selectedOptions,
  setSelectedOptions,
}) => {
  const isSelected = selectedOptions.includes(option);

  const handlePress = () => {
    setSelectedOptions((prevOptions) =>
      isSelected ? prevOptions.filter((opt) => opt !== option) : [...prevOptions, option]
    );
  };

  return (
    <Chip
      label={option}
      onClick={handlePress}
      sx={{
        margin: '5px',
        backgroundColor: isSelected ? 'primary.main' : 'neutral.50',
        color: isSelected ? 'white' : 'text.primary',
      }}
    />
  );
};

const ToggleSingleOption: React.FC<ToggleSingleOptionProps> = ({
  option,
  selectedOption,
  setSelectedOption,
}) => {
  const isSelected = selectedOption === option;

  const handlePress = () => {
    setSelectedOption(isSelected ? null : option);
  };

  return (
    <Chip
      label={option}
      onClick={handlePress}
      sx={{
        margin: '5px',
        backgroundColor: isSelected ? 'primary.main' : 'neutral.50',
        color: isSelected ? 'white' : 'text.primary',
      }}
    />
  );
};

const Preferences = () => {
  const dispatch = useDispatch();

  const [successMessage, setSuccessMessage] = useState<boolean>(false);

  const { data: userData } = useSelector(selectLoginState);
  const {
    data: userPreferences,
    status,
    loading,
  } = useRequestState({
    stateSelector: selectUserPreferences,
    errorShown: true,
    successMessageShown: successMessage,
  });

  const {
    favoriteCuisines,
    dietaryRestrictions,
    allergies,
    spiceLevel,
    mealPrepTime,
    comfortLevel,
  }: IUserPreferences = userPreferences;

  const [selectedCuisines, setSelectedCuisines] = useState<string[]>([]);
  const [selectedRestrictions, setSelectedRestrictions] = useState<string[]>([]);
  const [selectedAllergies, setSelectedAllergies] = useState<string[]>([]);
  const [selectedSpiceLevel, setSelectedSpiceLevel] = useState<string | null>(null);
  const [selectedPrepTime, setSelectedPrepTime] = useState<string | null>(null);
  const [selectedComfortLevel, setSelectedComfortLevel] = useState<string | null>(null);

  const updating = false;

  const onPressSave = () => {
    setSuccessMessage(true);
    dispatch(
      updateUserPreferences({
        favoriteCuisines: selectedCuisines,
        dietaryRestrictions: selectedRestrictions,
        allergies: selectedAllergies,
        spiceLevel: selectedSpiceLevel!,
        mealPrepTime: selectedPrepTime!,
        comfortLevel: selectedComfortLevel!,
      })
    );
  };

  useEffect(() => {
    setSelectedCuisines(favoriteCuisines);
    setSelectedRestrictions(dietaryRestrictions);
    setSelectedAllergies(allergies);
    setSelectedSpiceLevel(spiceLevel);
    setSelectedPrepTime(mealPrepTime);
    setSelectedComfortLevel(comfortLevel);
  }, [userPreferences]);

  useEffect(() => {
    if (userData?.id) {
      setSuccessMessage(false);
      dispatch(getUserPreferences({ userId: userData.id }));
    }
  }, [dispatch, userData?.id]);

  return (
    <Box sx={{ flex: 1, padding: '20px' }}>
      <Typography variant="h4" sx={{ marginBottom: '10px', textAlign: 'center' }}>
        {Strings.pageTitle.preferences}
      </Typography>

      <Box>
        <Typography variant="body2" color={'textDisabled'} sx={{ marginBottom: '10px' }}>
          {Strings.common.selectYourPreference}
        </Typography>

        {/* Favorite Cuisines */}
        <Box sx={{ marginBottom: '20px' }}>
          <Typography variant="subtitle1">{Strings.common.favoriteCuisines}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            {FAVORITE_CUISINES.map((cuisine) => (
              <ToggleOption
                key={cuisine}
                option={cuisine}
                selectedOptions={selectedCuisines}
                setSelectedOptions={setSelectedCuisines}
              />
            ))}
          </Box>
        </Box>

        {/* Dietary Restrictions */}
        <Box sx={{ marginBottom: '20px' }}>
          <Typography variant="subtitle1">{Strings.common.dietaryRestrictions}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            {DIETARY_RESTRICTIONS.map((restriction) => (
              <ToggleOption
                key={restriction}
                option={restriction}
                selectedOptions={selectedRestrictions}
                setSelectedOptions={setSelectedRestrictions}
              />
            ))}
          </Box>
        </Box>

        {/* Allergies */}
        <Box sx={{ marginBottom: '20px' }}>
          <Typography variant="subtitle1">{Strings.common.allergies}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            {ALlERGIES.map((allergy) => (
              <ToggleOption
                key={allergy}
                option={allergy}
                selectedOptions={selectedAllergies}
                setSelectedOptions={setSelectedAllergies}
              />
            ))}
          </Box>
        </Box>

        {/* Adventurous with Spices */}
        <Box sx={{ marginBottom: '20px' }}>
          <Typography variant="subtitle1">{Strings.common.adventurousWithSpices}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            {ADVENTUROUS.map((level) => (
              <ToggleSingleOption
                key={level}
                option={level}
                selectedOption={selectedSpiceLevel}
                setSelectedOption={setSelectedSpiceLevel}
              />
            ))}
          </Box>
        </Box>

        {/* Meal Prep Time */}
        <Box sx={{ marginBottom: '20px' }}>
          <Typography variant="subtitle1">{Strings.common.mealPrepTime}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            {MEAL_PREPARING_TIME.map((time) => (
              <ToggleSingleOption
                key={time}
                option={time}
                selectedOption={selectedPrepTime}
                setSelectedOption={setSelectedPrepTime}
              />
            ))}
          </Box>
        </Box>

        {/* Comfort Level */}
        <Box sx={{ marginBottom: '20px' }}>
          <Typography variant="subtitle1">{Strings.common.comfortLevel}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            {COMFORT_LEVEL.map((level) => (
              <ToggleSingleOption
                key={level}
                option={level}
                selectedOption={selectedComfortLevel}
                setSelectedOption={setSelectedComfortLevel}
              />
            ))}
          </Box>
        </Box>

        <Box sx={{ textAlign: 'center', marginTop: '20px' }}>
          <LoadingButton
            loading={loading}
            variant="contained"
            color="primary"
            onClick={onPressSave}
            disabled={updating}
            sx={{
              borderRadius: '50px',
            }}
          >
            {Strings.button.saveMyPreferences}
          </LoadingButton>
        </Box>
      </Box>
    </Box>
  );
};

export { Preferences };
